import {
	buildMapLayoutAttemptSeed,
	type ComputeMapLayoutOptions,
	computeMapLayout,
} from "./layout-engine";
import type { MapLayout, ModuleMapGraph } from "./map-graph-types";

export interface ResolvedHexagonMapLayout {
	layout: MapLayout;
	resolvedSeed: string;
	/** True when exact mode failed and search / straight-spine recovered the layout. */
	selfHealed: boolean;
}

export const HEXAGON_MAP_SEARCH_SEED_LIMIT = 6;

/**
 * Strip search (`:sN`) and attempt (`:attempt:N`) suffixes so structure changes can
 * re-search within the same seed family instead of pinning a dead exact attempt.
 */
export function stripMapLayoutSeedDerivatives(seed: string): string {
	let next = seed.trim();
	let previous = "";
	while (next !== previous) {
		previous = next;
		next = next.replace(/:attempt:\d+$/u, "").replace(/:s\d+$/u, "");
	}
	return next;
}

export function buildSearchCandidateSeed(seed: string, index: number): string {
	return index === 0 ? seed : `${seed}:s${index}`;
}

function logHexagonMapResolveEvent(
	event: string,
	payload: Record<string, unknown>,
): void {
	console.info(`[hexagon-map] ${event}`, payload);
}

function resolveViaSearch(params: {
	graph: ModuleMapGraph;
	layoutSeed: string;
	layoutOptions?: Omit<ComputeMapLayoutOptions, "seed">;
	layoutSeedMode: "exact" | "search";
	selfHealed: boolean;
}): ResolvedHexagonMapLayout {
	const {
		graph,
		layoutSeed,
		layoutOptions,
		layoutSeedMode,
		selfHealed,
	} = params;
	let lastError: unknown;
	const startedAt = Date.now();
	const searchBaseSeed = stripMapLayoutSeedDerivatives(layoutSeed);
	logHexagonMapResolveEvent("resolve layout start", {
		layoutSeedMode,
		layoutSeed,
		searchBaseSeed,
		selfHealed,
		graphNodeCount: graph.nodes.length,
		branchCorridorCount: graph.branchCorridorIds.length,
		searchSeedLimit: HEXAGON_MAP_SEARCH_SEED_LIMIT,
	});
	for (
		let candidateIndex = 0;
		candidateIndex < HEXAGON_MAP_SEARCH_SEED_LIMIT;
		candidateIndex += 1
	) {
		const candidateSeed = buildSearchCandidateSeed(
			searchBaseSeed,
			candidateIndex,
		);
		const candidateStartedAt = Date.now();
		try {
			const layout = computeMapLayout(graph, {
				...layoutOptions,
				seed: candidateSeed,
				exactSeed: false,
				forceStraightSpine: layoutOptions?.forceStraightSpine,
				logAttempts: layoutOptions?.logAttempts ?? true,
			});
			const resolvedSeed = buildMapLayoutAttemptSeed(
				candidateSeed,
				layout.generationAttempt ?? 1,
			);
			logHexagonMapResolveEvent("resolve layout success", {
				layoutSeedMode,
				layoutSeed,
				candidateSeed,
				candidateIndex,
				resolvedSeed,
				selfHealed,
				generationAttempt: layout.generationAttempt ?? 1,
				rows: layout.rows,
				cols: layout.cols,
				candidateElapsedMs: Date.now() - candidateStartedAt,
				totalElapsedMs: Date.now() - startedAt,
			});
			return {
				layout,
				resolvedSeed,
				selfHealed,
			};
		} catch (error) {
			lastError = error;
			logHexagonMapResolveEvent("resolve layout candidate failed", {
				layoutSeedMode,
				layoutSeed,
				candidateSeed,
				candidateIndex,
				selfHealed,
				candidateElapsedMs: Date.now() - candidateStartedAt,
				totalElapsedMs: Date.now() - startedAt,
				error: error instanceof Error ? error.message : String(error),
			});
		}
	}

	// Last-resort continuity: always show a valid map rather than blocking learners.
	if (!layoutOptions?.forceStraightSpine) {
		const fallbackStartedAt = Date.now();
		try {
			const layout = computeMapLayout(graph, {
				...layoutOptions,
				seed: searchBaseSeed,
				exactSeed: false,
				forceStraightSpine: true,
				logAttempts: layoutOptions?.logAttempts ?? true,
			});
			const resolvedSeed = buildMapLayoutAttemptSeed(
				`${searchBaseSeed}:straight`,
				layout.generationAttempt ?? 1,
			);
			logHexagonMapResolveEvent("resolve layout straight-spine fallback", {
				layoutSeedMode,
				layoutSeed,
				resolvedSeed,
				selfHealed: true,
				rows: layout.rows,
				cols: layout.cols,
				fallbackElapsedMs: Date.now() - fallbackStartedAt,
				totalElapsedMs: Date.now() - startedAt,
			});
			return {
				layout,
				resolvedSeed,
				selfHealed: true,
			};
		} catch (error) {
			lastError = error;
			logHexagonMapResolveEvent("resolve layout straight-spine failed", {
				layoutSeedMode,
				layoutSeed,
				totalElapsedMs: Date.now() - startedAt,
				error: error instanceof Error ? error.message : String(error),
			});
		}
	}

	logHexagonMapResolveEvent("resolve layout failed", {
		layoutSeedMode,
		layoutSeed,
		totalElapsedMs: Date.now() - startedAt,
		error:
			lastError instanceof Error ? lastError.message : String(lastError),
	});
	throw lastError instanceof Error
		? lastError
		: new Error("Failed to resolve hexagon map layout.");
}

/**
 * Resolve a hexagon map layout.
 *
 * Exact mode tries the persisted seed once. On failure it silently self-heals by
 * searching within the same seed family (stripped of `:attempt` / `:sN` suffixes),
 * then falls back to a straight spine so learners never hit a dead-end modal.
 */
export function resolveHexagonMapLayout(params: {
	graph: ModuleMapGraph;
	layoutSeed: string;
	layoutSeedMode: "exact" | "search";
	layoutOptions?: Omit<ComputeMapLayoutOptions, "seed">;
}): ResolvedHexagonMapLayout {
	const { graph, layoutSeed, layoutOptions, layoutSeedMode } = params;
	if (layoutSeedMode === "exact") {
		const startedAt = Date.now();
		logHexagonMapResolveEvent("resolve layout start", {
			layoutSeedMode,
			layoutSeed,
			graphNodeCount: graph.nodes.length,
			branchCorridorCount: graph.branchCorridorIds.length,
		});
		try {
			const layout = computeMapLayout(graph, {
				...layoutOptions,
				seed: layoutSeed,
				exactSeed: true,
				logAttempts: layoutOptions?.logAttempts ?? true,
			});
			logHexagonMapResolveEvent("resolve layout success", {
				layoutSeedMode,
				layoutSeed,
				resolvedSeed: layoutSeed,
				selfHealed: false,
				generationAttempt: layout.generationAttempt ?? 1,
				rows: layout.rows,
				cols: layout.cols,
				elapsedMs: Date.now() - startedAt,
			});
			return {
				layout,
				resolvedSeed: layoutSeed,
				selfHealed: false,
			};
		} catch (error) {
			logHexagonMapResolveEvent("resolve layout exact failed; self-heal", {
				layoutSeedMode,
				layoutSeed,
				elapsedMs: Date.now() - startedAt,
				error: error instanceof Error ? error.message : String(error),
			});
			return resolveViaSearch({
				graph,
				layoutSeed,
				layoutOptions,
				layoutSeedMode,
				selfHealed: true,
			});
		}
	}

	return resolveViaSearch({
		graph,
		layoutSeed,
		layoutOptions,
		layoutSeedMode,
		selfHealed: false,
	});
}
